/**
 * All money in this codebase is stored and computed as an integer number of
 * paise (INR minor units). Field names carrying money always end in `Minor`.
 *
 * Rationale: floating-point rupees accumulate error across tier pricing,
 * percentage option modifiers, discounts and GST splits, and Razorpay's API
 * takes paise anyway. Converting at the display/entry boundary only.
 */
/** Rupees (possibly fractional, e.g. from a CSV) -> integer paise. */
export function toMinor(rupees) {
    if (!Number.isFinite(rupees))
        throw new RangeError(`toMinor: not a finite number: ${rupees}`);
    return Math.round(rupees * 100);
}
/** Integer paise -> rupees as a number. Display only; never compute with this. */
export function toMajor(minor) {
    return minor / 100;
}
/** Rounds to whole paise using half-up, which is what Indian invoicing expects. */
export function roundMinor(value) {
    return Math.sign(value) * Math.round(Math.abs(value));
}
/** Applies a percentage to a minor amount, rounded to whole paise. */
export function percentOf(minor, percent) {
    return roundMinor((minor * percent) / 100);
}
export function sumMinor(values) {
    return values.reduce((acc, v) => acc + v, 0);
}
/*
 * Two formatters, chosen per amount, because one cannot be right for both.
 *
 * A single formatter with min 0 / max 2 digits rendered 40050 as "₹400.5" — a
 * price with one decimal place, which reads as a typo or a truncation rather
 * than as four hundred rupees and fifty paise. Min 2 / max 2 instead spells
 * every round figure "₹200.00", which is noise on a page of whole-rupee prices.
 *
 * So: paise are shown in full when there are any, and omitted when there are
 * none. This is what the docstring below has always claimed the function did.
 */
const INR_WHOLE = new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
});
const INR_PAISE = new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
});
/** "₹1,234.50", or "₹1,234" when the amount is whole rupees — for UI. */
export function formatMinor(minor) {
    const major = toMajor(minor);
    return (Math.round(minor) % 100 === 0 ? INR_WHOLE : INR_PAISE).format(major);
}
/**
 * Splits a total into `n` parts that sum back to exactly the total,
 * distributing the remainder paise across the first parts. Used when
 * apportioning an order-level discount across line items.
 */
export function allocateMinor(total, weights) {
    const weightSum = weights.reduce((a, b) => a + b, 0);
    if (weightSum <= 0)
        return weights.map(() => 0);
    const raw = weights.map((w) => (total * w) / weightSum);
    const floored = raw.map((v) => Math.floor(v));
    let remainder = total - floored.reduce((a, b) => a + b, 0);
    // Hand the leftover paise to the largest fractional parts first.
    const order = raw
        .map((v, i) => ({ i, frac: v - Math.floor(v) }))
        .sort((a, b) => b.frac - a.frac);
    const out = [...floored];
    for (const { i } of order) {
        if (remainder <= 0)
            break;
        out[i] = (out[i] ?? 0) + 1;
        remainder -= 1;
    }
    return out;
}
//# sourceMappingURL=money.js.map